export type SubjectCode = 'yuwen' | 'math' | 'english' | 'other';
export type LearningEpisodeStream = string;

export type LearningEpisodeStreamCardDecoration =
  | 'homepage-question'
  | 'opening'
  | 'family-summary'
  | 'ignore-sub-questions'
  | 'memory-per-card';

export type LearningEpisodeStreamRegistryEntry = {
  streamKey?: string;
  displayName?: string;
  /** AppServerConsole 可编辑；前台「关于此 Stream」展示 */
  description?: string;
  status?: 'active' | 'placeholder' | string;
  supportedSubjectCodes?: string[];
  subjectOrderMap?: Record<string, number>;
  decorationKeys?: string[];
};

export type SubjectConfigEntry = {
  label: string;
  char: string;
  color: string;
  streams: LearningEpisodeStream[];
};

export type StreamConfigEntry = {
  label: string;
  /** 英文展示名（可选；用于识别 deliberate practice 等） */
  labelEn?: string;
  /** 意义与构建逻辑；来自 Console description 下发 */
  description?: string;
  active: boolean;
  subjectOrderMap: Partial<Record<SubjectCode, number>>;
  familyDecorations: LearningEpisodeStreamCardDecoration[];
};

const FALLBACK_SUBJECT_STREAMS: Record<SubjectCode, LearningEpisodeStream[]> = {
  yuwen: ['flashcard_drill', 'recommended', 'specialized', 'classical_chinese', 'reading', 'pas_deliberate_practice'],
  math: ['flashcard_drill', 'recommended', 'specialized', 'pas_deliberate_practice'],
  english: ['flashcard_drill', 'recommended', 'specialized', 'vocabulary', 'interpreting', 'pas_deliberate_practice'],
  other: [],
};

const FALLBACK_STREAM_CONFIG: Record<string, StreamConfigEntry> = {
  flashcard_drill: {
    label: '打卡',
    description: '当前默认学习流：按日常或冲刺模式切换 Provider 内核，完成每日打卡式复习。',
    active: true,
    subjectOrderMap: { yuwen: 1, math: 1, english: 1 },
    familyDecorations: ['opening', 'family-summary', 'ignore-sub-questions'],
  },
  recommended: {
    label: '推荐序列',
    description: '按最近学习信号与学科颗粒覆盖做日常推荐。',
    active: false,
    subjectOrderMap: { yuwen: 2, math: 2, english: 2 },
    familyDecorations: ['family-summary'],
  },
  specialized: {
    label: '专项强化',
    description: '围绕同一局部主题或标签群做更密集的推进。',
    active: false,
    subjectOrderMap: { yuwen: 3, math: 3, english: 3 },
    familyDecorations: ['family-summary'],
  },
  vocabulary: {
    label: '词汇专项',
    description: '英语词汇专项复习流。',
    active: false,
    subjectOrderMap: { english: 5 },
    familyDecorations: [],
  },
  interpreting: {
    label: '口译',
    labelEn: 'Interpreting',
    description: '英语口译复述：正面主问与子问都是 Basic English，翻面写出目标英文复述。',
    active: true,
    subjectOrderMap: { english: 6 },
    familyDecorations: [],
  },
  classical_chinese: {
    label: '文言文专项',
    description: '语文文言文专项复习流。',
    active: false,
    subjectOrderMap: { yuwen: 5 },
    familyDecorations: [],
  },
  reading: {
    label: '阅读理解',
    description: '阅读理解专项流。',
    active: false,
    subjectOrderMap: { yuwen: 6, english: 6 },
    familyDecorations: [],
  },
  pas_deliberate_practice: {
    label: '刻意练习',
    description: '由实战答题纸局部框选孵化的练习题；同孵化去重，FSRS 紧迫度优先。测评档为熟悉 / 模糊 / 忘记。',
    active: true,
    subjectOrderMap: { yuwen: 6, math: 5, english: 5 },
    familyDecorations: ['memory-per-card'],
  },
};

function cloneStreamConfigEntry(entry: StreamConfigEntry): StreamConfigEntry {
  return {
    ...entry,
    subjectOrderMap: { ...entry.subjectOrderMap },
    familyDecorations: entry.familyDecorations.slice(),
  };
}

function buildSubjectConfigState(): Record<SubjectCode, SubjectConfigEntry> {
  return {
    yuwen: {
      label: '语文',
      char: '语',
      color: '#C50F1F',
      streams: FALLBACK_SUBJECT_STREAMS.yuwen.slice(),
    },
    math: {
      label: '数学',
      char: '数',
      color: '#0F6CBD',
      streams: FALLBACK_SUBJECT_STREAMS.math.slice(),
    },
    english: {
      label: '英语',
      char: '英',
      color: '#038387',
      streams: FALLBACK_SUBJECT_STREAMS.english.slice(),
    },
    other: {
      label: '其他',
      char: '其',
      color: '#64748B',
      streams: FALLBACK_SUBJECT_STREAMS.other.slice(),
    },
  };
}

function buildStreamConfigState(): Record<string, StreamConfigEntry> {
  return {
    flashcard_drill: cloneStreamConfigEntry(FALLBACK_STREAM_CONFIG.flashcard_drill),
    recommended: cloneStreamConfigEntry(FALLBACK_STREAM_CONFIG.recommended),
    specialized: cloneStreamConfigEntry(FALLBACK_STREAM_CONFIG.specialized),
    vocabulary: cloneStreamConfigEntry(FALLBACK_STREAM_CONFIG.vocabulary),
    interpreting: cloneStreamConfigEntry(FALLBACK_STREAM_CONFIG.interpreting),
    classical_chinese: cloneStreamConfigEntry(FALLBACK_STREAM_CONFIG.classical_chinese),
    reading: cloneStreamConfigEntry(FALLBACK_STREAM_CONFIG.reading),
    pas_deliberate_practice: cloneStreamConfigEntry(FALLBACK_STREAM_CONFIG.pas_deliberate_practice),
  };
}

export const SUBJECT_CONFIG: Record<SubjectCode, SubjectConfigEntry> = buildSubjectConfigState();
export const STREAM_CONFIG: Record<string, StreamConfigEntry> = buildStreamConfigState();

/** 语 / 数 / 外 — LEC 与学习流主学科 */
export const CORE_SUBJECT_CODES: Array<'yuwen' | 'math' | 'english'> = ['yuwen', 'math', 'english'];
export const SUBJECT_CODES: SubjectCode[] = ['yuwen', 'math', 'english', 'other'];
export const DEFAULT_SUBJECT: SubjectCode = 'math';
export const DEFAULT_STREAM: LearningEpisodeStream = 'flashcard_drill';

/** 可加载的学科引擎。数学为必选基础模块；语文 / 英语为可选 Beta 模块。 */
export type SubjectModuleCode = 'yuwen' | 'math' | 'english';
export type OptionalSubjectModuleCode = 'yuwen' | 'english';
export const REQUIRED_SUBJECT_MODULE: SubjectModuleCode = 'math';
export const OPTIONAL_SUBJECT_MODULE_CODES: OptionalSubjectModuleCode[] = ['yuwen', 'english'];
export const SUBJECT_MODULE_CODES: SubjectModuleCode[] = ['math', 'yuwen', 'english'];
export type LoadedSubjectModules = Record<OptionalSubjectModuleCode, boolean>;
export const DEFAULT_LOADED_SUBJECT_MODULES: LoadedSubjectModules = {
  yuwen: true,
  english: true,
};

export function isCoreSubjectCode(value: unknown): value is 'yuwen' | 'math' | 'english' {
  return value === 'yuwen' || value === 'math' || value === 'english';
}

export function isOptionalSubjectModuleCode(value: unknown): value is OptionalSubjectModuleCode {
  return value === 'yuwen' || value === 'english';
}

export function normalizeLoadedSubjectModules(value: unknown): LoadedSubjectModules {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    yuwen: source.yuwen !== false,
    english: source.english !== false,
  };
}

export function isSubjectModuleLoaded(code: SubjectCode, loaded: LoadedSubjectModules): boolean {
  if (code === 'math' || code === 'other') {
    return true;
  }
  if (isOptionalSubjectModuleCode(code)) {
    return loaded[code];
  }
  return false;
}

export function listPickerSubjectCodes(loaded: LoadedSubjectModules): SubjectCode[] {
  return SUBJECT_CODES.filter((code) => isSubjectModuleLoaded(code, loaded));
}

export function coerceActiveSubjectToLoaded(code: SubjectCode, loaded: LoadedSubjectModules): SubjectCode {
  if (isSubjectModuleLoaded(code, loaded)) {
    return code;
  }
  return DEFAULT_SUBJECT;
}

export function isValidSubjectCode(value: unknown): value is SubjectCode {
  return isCoreSubjectCode(value) || value === 'other';
}

export function normalizeSubjectCode(value: unknown): SubjectCode {
  if (isValidSubjectCode(value)) {
    return value;
  }
  return DEFAULT_SUBJECT;
}

export function resolveSubjectCode(value: unknown): SubjectCode | null {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return null;
  }

  if (isValidSubjectCode(raw)) {
    return raw;
  }

  const normalized = raw.toLowerCase();
  if (normalized === 'chinese' || normalized === 'cn') {
    return 'yuwen';
  }
  if (normalized === 'math' || normalized === 'mathematics') {
    return 'math';
  }
  if (normalized === 'english' || normalized === 'en') {
    return 'english';
  }
  if (normalized === 'other' || raw === '其他' || raw === '其它') {
    return 'other';
  }

  const matched = CORE_SUBJECT_CODES.find(
    (code) => raw === SUBJECT_CONFIG[code].label
      || raw === SUBJECT_CONFIG[code].char
      || raw.includes(SUBJECT_CONFIG[code].label),
  );

  return matched ?? null;
}

export function resolveSubjectCodeFromCandidates(...values: unknown[]): SubjectCode | null {
  for (const value of values) {
    const resolved = resolveSubjectCode(value);
    if (resolved) {
      return resolved;
    }
  }
  return null;
}

/** KP-auxiliary photos stored in `assets` but not user study materials. */
const HOME_DIARY_EXCLUDED_ASSET_SOURCES = new Set([
  'factory_figure_extract',
  'geometry_candidate',
  'dajiao_geometry_regen',
]);

const HOME_DIARY_VISIBLE_PURPOSES = new Set([
  'learning_diary',
  'pas',
]);

export function isHomeDiaryAsset(asset: {
  metadata?: unknown;
  subject?: unknown;
  description?: unknown;
}): boolean {
  const meta = asset.metadata && typeof asset.metadata === 'object' && !Array.isArray(asset.metadata)
    ? asset.metadata as Record<string, unknown>
    : null;
  if (meta && meta.homeDiaryVisible === false) {
    return false;
  }
  const purpose = String(meta?.purpose || '').trim().toLowerCase();
  if (purpose && !HOME_DIARY_VISIBLE_PURPOSES.has(purpose)) {
    return false;
  }
  const source = String(meta?.source || '').trim().toLowerCase();
  if (source && HOME_DIARY_EXCLUDED_ASSET_SOURCES.has(source)) {
    return false;
  }
  if (meta && String(meta.factoryFigureKey || '').trim()) {
    return false;
  }
  const subject = String(asset.subject ?? '').trim().toLowerCase();
  if (subject.startsWith('factory-figure') || subject.startsWith('factory_figure')) {
    return false;
  }
  const description = String(asset.description ?? '').trim();
  if (description.startsWith('工厂插图')) {
    return false;
  }
  return true;
}

function buildFallbackStreamLabel(stream: LearningEpisodeStream): string {
  const normalized = String(stream || '').trim();
  if (!normalized) {
    return '学习方式';
  }

  return normalized.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
}

function normalizePositiveStreamOrder(value: unknown): number | null {
  const numeric = Math.floor(Number(value));
  if (!Number.isFinite(numeric) || numeric < 1) {
    return null;
  }
  return numeric;
}

function normalizeStreamSubjectOrderMap(
  value: unknown,
  fallback?: Partial<Record<SubjectCode, number>>,
): Partial<Record<SubjectCode, number>> {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const next: Partial<Record<SubjectCode, number>> = {};
  for (const subject of CORE_SUBJECT_CODES) {
    const normalized = normalizePositiveStreamOrder(source[subject] ?? fallback?.[subject]);
    if (normalized !== null) {
      next[subject] = normalized;
    }
  }
  return next;
}

function normalizeStreamDecorationKey(value: unknown): LearningEpisodeStreamCardDecoration | null {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'homepage_question' || normalized === 'homepage-question') {
    return 'homepage-question';
  }
  if (normalized === 'opening') {
    return 'opening';
  }
  if (normalized === 'family_summary' || normalized === 'family-summary') {
    return 'family-summary';
  }
  if (normalized === 'ignore_sub_questions' || normalized === 'ignore-sub-questions') {
    return 'ignore-sub-questions';
  }
  if (normalized === 'memory_per_card' || normalized === 'memory-per-card') {
    return 'memory-per-card';
  }
  return null;
}

function dedupeStreams(streams: LearningEpisodeStream[]): LearningEpisodeStream[] {
  const seen = new Set<string>();
  const next: LearningEpisodeStream[] = [];

  for (const stream of streams) {
    const normalized = String(stream || '').trim();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }

  return next;
}

function resetRuntimeStreamRegistry() {
  const nextSubjectConfig = buildSubjectConfigState();
  for (const code of SUBJECT_CODES) {
    SUBJECT_CONFIG[code].streams = nextSubjectConfig[code].streams;
  }

  for (const key of Object.keys(STREAM_CONFIG)) {
    delete STREAM_CONFIG[key];
  }

  const nextStreamConfig = buildStreamConfigState();
  for (const [key, value] of Object.entries(nextStreamConfig)) {
    STREAM_CONFIG[key] = value;
  }
}

export function applyLearningEpisodeStreamRegistry(entries: LearningEpisodeStreamRegistryEntry[]) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return;
  }

  resetRuntimeStreamRegistry();
  for (const code of SUBJECT_CODES) {
    SUBJECT_CONFIG[code].streams = [];
  }

  for (const entry of entries) {
    const streamKey = String(entry?.streamKey || '').trim();
    if (!streamKey) {
      continue;
    }

    const currentConfig = STREAM_CONFIG[streamKey];
    const decorationKeys = Array.isArray(entry.decorationKeys) ? entry.decorationKeys : null;
    let decorations = decorationKeys
      ? decorationKeys
        .map((value) => normalizeStreamDecorationKey(value))
        .filter((value): value is LearningEpisodeStreamCardDecoration => Boolean(value))
      : currentConfig?.familyDecorations.slice() || [];
    const zhLabel = String(
      entry.displayName || currentConfig?.label || buildFallbackStreamLabel(streamKey),
    ).trim() || buildFallbackStreamLabel(streamKey);
    if (
      streamKey === 'pas_deliberate_practice'
      || /deliberate_practice/.test(streamKey)
      || zhLabel === '刻意练习'
    ) {
      decorations = ['memory-per-card'];
    }

    const description = String(entry.description ?? currentConfig?.description ?? '').trim();

    STREAM_CONFIG[streamKey] = {
      label: zhLabel,
      description: description || undefined,
      active: String(entry.status || '').trim().toLowerCase() === 'active',
      subjectOrderMap: normalizeStreamSubjectOrderMap(entry.subjectOrderMap, currentConfig?.subjectOrderMap),
      familyDecorations: decorations,
    };

    const supportedSubjects = Array.isArray(entry.supportedSubjectCodes)
      ? entry.supportedSubjectCodes.filter((subject): subject is SubjectCode => isCoreSubjectCode(subject))
      : [];

    for (const subject of supportedSubjects) {
      SUBJECT_CONFIG[subject].streams = dedupeStreams([...SUBJECT_CONFIG[subject].streams, streamKey]);
    }
  }
}

function preferStreamWhenDuplicateLabel(left: LearningEpisodeStream, right: LearningEpisodeStream): LearningEpisodeStream {
  if (left === 'pas_deliberate_practice') return left;
  if (right === 'pas_deliberate_practice') return right;
  const leftDecorations = getStreamConfig(left).familyDecorations;
  const rightDecorations = getStreamConfig(right).familyDecorations;
  if (leftDecorations.includes('memory-per-card') && !rightDecorations.includes('memory-per-card')) {
    return left;
  }
  if (rightDecorations.includes('memory-per-card') && !leftDecorations.includes('memory-per-card')) {
    return right;
  }
  return left;
}

function dedupeStreamsByDisplayLabel(streams: LearningEpisodeStream[]): LearningEpisodeStream[] {
  const chosen = new Map<string, LearningEpisodeStream>();
  const order: string[] = [];

  for (const stream of streams) {
    const labelKey = getStreamConfig(stream).label.trim().toLowerCase() || stream;
    const existing = chosen.get(labelKey);
    if (!existing) {
      chosen.set(labelKey, stream);
      order.push(labelKey);
      continue;
    }
    chosen.set(labelKey, preferStreamWhenDuplicateLabel(existing, stream));
  }

  return order.map((key) => chosen.get(key)!).filter(Boolean);
}

export function getAvailableStreamsForSubject(subject: SubjectCode): LearningEpisodeStream[] {
  const streams = dedupeStreamsByDisplayLabel(
    SUBJECT_CONFIG[subject].streams.filter((stream) => getStreamConfig(stream).active),
  );
  return streams.sort((left, right) => {
    const leftConfig = getStreamConfig(left);
    const rightConfig = getStreamConfig(right);
    const leftOrder = normalizePositiveStreamOrder(leftConfig.subjectOrderMap[subject]) ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = normalizePositiveStreamOrder(rightConfig.subjectOrderMap[subject]) ?? Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return leftConfig.label.localeCompare(rightConfig.label, 'zh-CN', { numeric: true, sensitivity: 'base' })
      || String(left).localeCompare(String(right), 'zh-CN', { numeric: true, sensitivity: 'base' });
  });
}

export function getDefaultStreamForSubject(subject: SubjectCode): LearningEpisodeStream {
  const subjectStreams = getAvailableStreamsForSubject(subject);
  return subjectStreams[0] || DEFAULT_STREAM;
}

export function getStreamConfig(stream: LearningEpisodeStream): StreamConfigEntry {
  const config = STREAM_CONFIG[stream];
  if (config) {
    return cloneStreamConfigEntry(config);
  }

  return {
    label: buildFallbackStreamLabel(stream),
    active: true,
    subjectOrderMap: {},
    familyDecorations: [],
  };
}

export function getStreamLabel(stream: LearningEpisodeStream): string {
  return getStreamConfig(stream).label;
}

/** Console 下发的 Stream 意义/构建说明；空则返回 '' */
export function getStreamDescription(stream: LearningEpisodeStream): string {
  return String(getStreamConfig(stream).description || '').trim();
}

/**
 * 刻意练习 Stream（含学科本地克隆 / 同名标签）：无码紧凑打印 + memory triad。
 * 不要只认精确 id — 否则英语等科会误走「实战答题纸」有码打印。
 */
export function isPasDeliberatePracticeStream(
  stream: LearningEpisodeStream | string | null | undefined,
): boolean {
  const key = String(stream || '').trim();
  if (!key) {
    return false;
  }
  if (key === 'pas_deliberate_practice') {
    return true;
  }
  if (/(^|_)pas_deliberate_practice$/.test(key) || /deliberate_practice/.test(key)) {
    return true;
  }

  const config = STREAM_CONFIG[key];
  if (config?.familyDecorations?.includes('memory-per-card')) {
    return true;
  }
  if (String(config?.label || '').trim() === '刻意练习') {
    return true;
  }
  if (String(config?.labelEn || '').trim().toLowerCase() === 'deliberate practice') {
    return true;
  }
  return false;
}

export function getStreamFamilyDecorations(stream: LearningEpisodeStream): LearningEpisodeStreamCardDecoration[] {
  // Product rule: 刻意练习 always uses blank-flip + triad (never lead/summary chrome).
  if (isPasDeliberatePracticeStream(stream)) {
    return ['memory-per-card'];
  }
  return getStreamConfig(stream).familyDecorations;
}

export function streamHasFamilyDecoration(
  stream: LearningEpisodeStream,
  decoration: LearningEpisodeStreamCardDecoration,
): boolean {
  return getStreamFamilyDecorations(stream).includes(decoration);
}

export function streamUsesOpeningCard(stream: LearningEpisodeStream): boolean {
  return streamHasFamilyDecoration(stream, 'opening');
}

export function streamUsesFamilySummaryCard(stream: LearningEpisodeStream): boolean {
  return streamHasFamilyDecoration(stream, 'family-summary');
}
